import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  onAuthStateChanged,
  signOut,
  updateProfile,
  getRedirectResult,
} from "firebase/auth";
import { auth, db } from "../firebase/firebaseConfig";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const ADMIN_EMAIL = "nahomnigussie7@gmail.com";
const INACTIVITY_LIMIT = 20 * 60 * 1000; // 20 minutes

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);
  const beforeUnloadRef = useRef(null);

  /** ────────────────────────────────────────────────
   * Fetch user data from Firestore
   * ──────────────────────────────────────────────── */
  const fetchFirestoreUser = async (uid) => {
    try {
      const userSnap = await getDoc(doc(db, "users", uid));
      return userSnap.exists() ? userSnap.data() : {};
    } catch (err) {
      console.error("Failed to fetch Firestore user data:", err);
      return {};
    }
  };

  /** ────────────────────────────────────────────────
   * Refresh user data manually
   * ──────────────────────────────────────────────── */
  const refreshUserFromFirestore = useCallback(async () => {
    if (!auth.currentUser) return;
    const firestoreData = await fetchFirestoreUser(auth.currentUser.uid);
    setUser((prev) => ({
      ...prev,
      ...firestoreData,
      bio: firestoreData.bio || "",
      phone: firestoreData.phone || "",
    }));
  }, []);

  /** ────────────────────────────────────────────────
   * Logout
   * ──────────────────────────────────────────────── */
  const logout = useCallback(async () => {
    try {
      if (auth.currentUser) {
        const userRef = doc(db, "users", auth.currentUser.uid);
        await setDoc(
          userRef,
          { online: false, lastSeen: serverTimestamp() },
          { merge: true }
        );
      }
      await signOut(auth);
      setUser(null);
      clearTimeout(inactivityTimerRef.current);
      window.removeEventListener("beforeunload", beforeUnloadRef.current);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, []);

  /** ────────────────────────────────────────────────
   * Update profile info
   * ──────────────────────────────────────────────── */
  const updateUserProfile = useCallback(async (profileData) => {
    try {
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: profileData.displayName,
          photoURL: profileData.photoURL,
        });
        setUser((prev) => ({
          ...prev,
          ...profileData,
        }));
      }
    } catch (error) {
      console.error("Failed to update profile:", error);
    }
  }, []);

  /** ────────────────────────────────────────────────
   * Reset inactivity timer
   * ──────────────────────────────────────────────── */
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      console.warn("User inactive. Logging out...");
      logout();
    }, INACTIVITY_LIMIT);
  }, [logout]);

  /** ────────────────────────────────────────────────
   * Inactivity listeners
   * ──────────────────────────────────────────────── */
  useEffect(() => {
    const events = ["mousemove", "keydown", "click", "touchstart"];
    const handleActivity = () => resetInactivityTimer();

    if (user) {
      events.forEach((event) => window.addEventListener(event, handleActivity));
      resetInactivityTimer();
    }

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      clearTimeout(inactivityTimerRef.current);
    };
  }, [user, resetInactivityTimer]);

  /** ────────────────────────────────────────────────
   * Handle Google redirect login
   * ──────────────────────────────────────────────── */
  useEffect(() => {
    const handleRedirectLogin = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          const user = result.user;
          const role =
            user.email.toLowerCase() === ADMIN_EMAIL ? "admin" : "user";
          const userRef = doc(db, "users", user.uid);
          await setDoc(
            userRef,
            { email: user.email, role, createdAt: serverTimestamp() },
            { merge: true }
          );
        }
      } catch (error) {
        console.error("Google redirect login failed:", error);
      }
    };

    if (window.location.pathname.includes("login")) {
      handleRedirectLogin();
    }
  }, []);

  /** ────────────────────────────────────────────────
   * Auth state listener (optimized)
   * ──────────────────────────────────────────────── */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          
        });
        setLoading(false); // show UI immediately

        // Async Firestore updates
        const userRef = doc(db, "users", firebaseUser.uid);
        Promise.all([
          setDoc(
            userRef,
            { online: true, lastSeen: serverTimestamp() },
            { merge: true }
          ),
          fetchFirestoreUser(firebaseUser.uid),
        ])
          .then(([_, firestoreData]) => {
            setUser((prev) => ({
              ...prev,
              ...firestoreData,
              isAdmin: firestoreData.role === "admin",
              role: firestoreData.role || "user",
              bio: firestoreData.bio || "",
              phone: firestoreData.phone || "",
            }));
          })
          .catch((err) => console.error("User sync failed:", err));

        // Track offline efficiently
        beforeUnloadRef.current = () =>
          navigator.sendBeacon(
            `https://firestore.googleapis.com/v1/projects/${db.projectId}/databases/(default)/documents/users/${firebaseUser.uid}`,
            JSON.stringify({ online: false, lastSeen: serverTimestamp() })
          );
        window.addEventListener("beforeunload", beforeUnloadRef.current);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  /** ────────────────────────────────────────────────
   * Context value
   * ──────────────────────────────────────────────── */
  const contextValue = useMemo(
    () => ({
      user,
      loading,
      logout,
      updateUserProfile,
      refreshUser: refreshUserFromFirestore,
    }),
    [user, loading, refreshUserFromFirestore, logout, updateUserProfile]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
